import type { EncodedAudioData, EncodedData, EncodedVideoData, EncodedVideoFrame } from '../mpegts/demuxerTypes';
import type { Fraction } from '../util/fractionType';
import { compareFractions, frac, multiplyFractionBy } from '../util/integerMath';
import { invariant } from '../util/invariant';
import { buildChunkIndexes } from './buildChunkIndexes';
import { buildSampleOffsets } from './buildSampleOffsets';
import { CompositionTimeHelper } from './compositionTimeHelper';
import { KeyframeHelper } from './keyframeHelper';
import type { Mp4AudioTrack, Mp4Info, Mp4VideoTrack, Sample, VirtualSegment } from './mp4Types';
import type { ChunkLargeOffsetBox, ChunkOffsetBox, CompactSampleSizeBox, CompositionOffsetBox, Mp4Box, SampleSizeBox, SampleToChunkBox, SyncSampleBox, TimeToSampleBox } from './parser';
import { SampleSizeHelper } from './sampleSizeHelper';

export function populateVirtualSegments(
  virtualSegments: VirtualSegment[] | null,
  // null if we have a vide track
  audioTrackIndex: number | null,
  mediaStartPtsOffset: number,
  timescale: number,
  stblBox: Mp4Box,
): VirtualSegment[] {
  const isVideo = audioTrackIndex === null;
  const stszBox = stblBox.children.find(b => b.type === 'stsz') as SampleSizeBox | undefined;
  const stz2Box = stblBox.children.find(b => b.type === 'stz2') as CompactSampleSizeBox | undefined;

  let sampleCount: number;
  let sampleSizeHelper: SampleSizeHelper;
  if (stszBox) {
    sampleCount = stszBox.sample_count;
    sampleSizeHelper = new SampleSizeHelper(stszBox.sample_size, stszBox.sample_sizes);
  } else if (stz2Box) {
    sampleCount = stz2Box.sample_count;
    sampleSizeHelper = new SampleSizeHelper(0, stz2Box.sample_sizes);
  } else {
    throw new Error('Neither stsz nor stz2 box was found');
  }

  const stscBox = stblBox.children.find(b => b.type === 'stsc') as SampleToChunkBox | undefined;
  invariant(stscBox, 'Missing stsc box');
  const chunkIndexes = buildChunkIndexes(stscBox.first_chunk, stscBox.samples_per_chunk, sampleCount);

  const stcoBox = stblBox.children.find(b => b.type === 'stco') as ChunkOffsetBox | undefined;
  const co64Box = stblBox.children.find(b => b.type === 'co64') as ChunkLargeOffsetBox | undefined;
  let chunkOffsets: number[];
  if (stcoBox) {
    chunkOffsets = stcoBox.chunk_offsets;
  } else if (co64Box) {
    chunkOffsets = co64Box.chunk_offsets;
  } else {
    throw new Error('Neither stco nor co64 box was found');
  }

  const sampleOffsets = buildSampleOffsets(sampleSizeHelper, chunkIndexes, chunkOffsets);


  const sttsBox = stblBox.children.find(b => b.type === 'stts') as TimeToSampleBox | undefined;
  invariant(sttsBox, 'Missing stts box');

  const cttsBox = stblBox.children.find(b => b.type === 'ctts') as CompositionOffsetBox | undefined;

  const sampleDuration = getSampleDuration(sttsBox);
  const sampleDurationInSeconds = frac(sampleDuration, timescale);

  // if we don't already have virtual segments, we create some based on the sync samples
  // which represent positions it is safe to start decoding from
  if (virtualSegments === null) {
    const syncSamples = getSyncSamples(stblBox, isVideo, sampleCount, timescale);
    virtualSegments = createEmptyVirtualSegments(syncSamples, sampleCount, sampleDurationInSeconds);
  }

  // Unlike createEmptyVirtualSegments() that can use a generated sync sample array,
  // the keyframe helper needs the original sync sample array
  const keyframeHelper = new KeyframeHelper((stblBox.children.find(b => b.type === 'stss') as SyncSampleBox | undefined)?.sample_number);

  const compositionTimeHelper = new CompositionTimeHelper(cttsBox?.sample_counts ?? [], cttsBox?.sample_offsets ?? []);
  const samples: Sample[] = [];
  let currentDts = -mediaStartPtsOffset;
  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
    const dts = currentDts;
    currentDts += sampleDuration;
    const pts = dts + compositionTimeHelper.getNextCompositionOffset();
    const keyframe = keyframeHelper.isKeyframe(sampleIndex);
    const size = sampleSizeHelper.getSampleSize(sampleIndex);
    const offset = sampleOffsets[sampleIndex];

    const sample: Sample = {
      dts,
      pts,
      offset,
      size,
      duration: sampleDuration,
      keyframe,
    };

    samples.push(sample);
  }

  // Finally, let's put the samples into the virtual segments
  dispatchSamplesIntoVirtualSegments(virtualSegments, samples, sampleDurationInSeconds, audioTrackIndex);

  return virtualSegments;
}


export function dispatchSamplesIntoVirtualSegments(
  virtualSegments: VirtualSegment[],
  samples: Sample[],
  sampleDurationInSeconds: Fraction,
  // null if we have a vide track
  audioTrackIndex: number | null,
): void {
  let currentVirtualSegmentIndex = 0;
  let currentVirtualSegment = virtualSegments[currentVirtualSegmentIndex];
  let sampleArray: Sample[];
  if (audioTrackIndex === null) {
    sampleArray = currentVirtualSegment.videoSamples;
  } else {
    currentVirtualSegment.audioSamples[audioTrackIndex] = [];
    sampleArray = currentVirtualSegment.audioSamples[audioTrackIndex];
  }

  for (let sampleIndex = 0; sampleIndex < samples.length; sampleIndex++) {
    const sample = samples[sampleIndex];
    const sampleStartTimeInSeconds = multiplyFractionBy(sampleDurationInSeconds, sampleIndex);

    if (compareFractions(sampleStartTimeInSeconds, currentVirtualSegment.exclusiveEndInSeconds) >= 0 && currentVirtualSegmentIndex < (virtualSegments.length - 1)) {
      // The current sample does not start before the end of the current segment,
      // time to move on to the next segment
      currentVirtualSegmentIndex++;
      currentVirtualSegment = virtualSegments[currentVirtualSegmentIndex];
      if (audioTrackIndex === null) {
        sampleArray = currentVirtualSegment.videoSamples;
      } else {
        currentVirtualSegment.audioSamples[audioTrackIndex] = [];
        sampleArray = currentVirtualSegment.audioSamples[audioTrackIndex];
      }
    }

    // Let's push the sample and update the virtual segment byte range to make sure it includes this sample's bytes
    sampleArray.push(sample);
    if (currentVirtualSegment.inclusiveByteRangeStart === -1 || sample.offset < currentVirtualSegment.inclusiveByteRangeStart) {
      currentVirtualSegment.inclusiveByteRangeStart = sample.offset;
    }
    const sampleEnd = sample.offset + sample.size;
    if (currentVirtualSegment.exclusiveByteRangeEnd === -1 || sampleEnd > currentVirtualSegment.exclusiveByteRangeEnd) {
      currentVirtualSegment.exclusiveByteRangeEnd = sampleEnd;
    }
  }
}


/**
 * Gets the sync sample array if present. If not, all samples are sync samples
 * and we generate an array with some arbitrary grouping.
 *
 * Note that the sync samples indexes are numbered from 1.
 */
function getSyncSamples(
  stblBox: Mp4Box,
  isVideo: boolean,
  sampleCount: number,
  timescale: number,
): number[] {
  const syncSamples = (stblBox.children.find(b => b.type === 'stss') as SyncSampleBox | undefined)?.sample_number;
  if (syncSamples !== undefined) {
    return syncSamples;
  }

  // If we don't have a sync sample box, every sample is a sync one. In that case, we create some
  // arbitrary groups of 10 video I-frames or about 10 seconds worth of audio AAC frames of 1024
  // samples
  const sampleGroupSize = isVideo ? 10 : Math.round((timescale * 10) / 1024);
  return makeSyncSampleArray(sampleGroupSize, sampleCount);
}


/**
 * Based on the given sync samples, this function creates an array of
 * empty virtual segments that are only defined by their start and end
 * time positions.
 */
export function createEmptyVirtualSegments(
  syncSamples: number[],
  sampleCount: number,
  sampleDurationInSeconds: Fraction,
): VirtualSegment[] {
  const virtualSegments: VirtualSegment[] = [];

  for (let i = 0; i < syncSamples.length; i++) {
    // We normalize the sample indexes to start at 0
    const inclusiveStartSample = syncSamples[i] - 1;
    const exclusiveEndSample = (i === (syncSamples.length - 1)) ? sampleCount : syncSamples[i + 1] - 1;

    const inclusiveStartInSeconds = multiplyFractionBy(sampleDurationInSeconds, inclusiveStartSample);
    const exclusiveEndInSeconds = multiplyFractionBy(sampleDurationInSeconds, exclusiveEndSample);
    virtualSegments.push({
      inclusiveStartInSeconds,
      exclusiveEndInSeconds,
      inclusiveByteRangeStart: -1,
      exclusiveByteRangeEnd: -1,
      videoSamples: [],
      audioSamples: [],
    });
  }

  return virtualSegments;
}


/**
 * Since we don't support variable framerates, we expect all samples to
 * have the same duration, except maybe the last one.
 */
function getSampleDuration(sttsBox: TimeToSampleBox) {
  const duration = sttsBox.sample_delta[0];
  for (let i = 1; i < sttsBox.sample_delta.length - 1; i++) {
    if (sttsBox.sample_delta[i] !== duration) {
      throw new Error(`Variable framerate: sample_delta[${i}]=${sttsBox.sample_delta[i]} not the same as sample_delta[0]=${duration}`);
    }
  }
  return duration;
}


export function makeSyncSampleArray(groupSize: number, nSamples: number): number[] {
  if (nSamples === 0) {
    return [];
  }

  // Samples are numbered from 1
  const firstSample = 1;
  const lastSample = nSamples;
  const syncSamples: number[] = [];
  for (let sample = firstSample; sample <= lastSample; sample += groupSize) {
    syncSamples.push(sample);
  }
  return syncSamples;
}


/**
 * Returns the index of the virtual segment containing the given position of -1 if no segment contains it.
 */
export function findVirtualSegmentIndex(
  position: Fraction,
  virtualSegments: VirtualSegment[],
): number {
  if (virtualSegments.length === 0) {
    // No segments at all
    return -1;
  }

  let first = 0;
  if (compareFractions(position, virtualSegments[first].inclusiveStartInSeconds) < 0) {
    // The position is before the inclusive start of the first segment
    return -1;
  }

  let last = virtualSegments.length - 1;
  if (compareFractions(position, virtualSegments[last].exclusiveEndInSeconds) >= 0) {
    // The position is >= the exclusive end of the last segment
    return -1;
  }

  // Now we know that that there is a segment that contains the given position
  while (first < last) {
    const middle = Math.round((first + last) / 2.0);
    const segment = virtualSegments[middle];
    if (compareFractions(position, segment.inclusiveStartInSeconds) < 0) {
      last = middle - 1;
      continue;
    }

    if (compareFractions(position, segment.exclusiveEndInSeconds) >= 0) {
      first = middle + 1;
      continue;
    }

    return middle;
  }

  invariant(first === last, 'Should have first === last');
  const segment = virtualSegments[first];
  invariant(compareFractions(position, segment.inclusiveStartInSeconds) >= 0, 'Position should be inside range');
  invariant(compareFractions(position, segment.exclusiveEndInSeconds) < 0, 'Position should be inside range');
  return first;
}


function buildEncodedVideoData(
  byteOffset: number,
  bytes: Uint8Array,
  videoSamples: Sample[],
  videoTrack: Mp4VideoTrack,
): EncodedVideoData {

  return {
    codec: 'avc1.42c020',
    description: videoTrack.avcC,
    width: videoTrack.width,
    height: videoTrack.height,
    frameDuration: videoTrack.frameDuration,
    frames: videoSamples.map<EncodedVideoFrame>(sample => ({
      pts: sample.pts,
      dts: sample.dts,
      duration: sample.duration,
      keyframe: sample.keyframe,
      data: bytes.slice(sample.offset - byteOffset, sample.offset - byteOffset + sample.size),
    })),
    timescale: videoTrack.timescale,
    epsilon: frac(0, 1),
  };
}


function buildEncodedAudioData(
  byteOffset: number,
  bytes: Uint8Array,
  audioSamples: Sample[],
  audioTrack: Mp4AudioTrack,
): EncodedAudioData {

  return {
    codec: 'mp4a.40.2',
    frames: audioSamples.map<EncodedVideoFrame>(sample => ({
      pts: sample.pts,
      dts: sample.dts,
      duration: sample.duration,
      keyframe: sample.keyframe,
      data: bytes.slice(sample.offset - byteOffset, sample.offset - byteOffset + sample.size),
    })),
    timescale: audioTrack.timescale,
    numberOfChannels: audioTrack.channelCount,
    channelConfiguration: -1,
    sampleRateInHz: audioTrack.sampleRate,
    epsilon: frac(0, 1),
  };
}


export function buildEncodedDataFromVirtualSegment(
  bytes: Uint8Array,
  virtualSegment: VirtualSegment,
  mp4Info: Mp4Info,
): EncodedData {
  const byteOffset = virtualSegment.inclusiveByteRangeStart;

  const encodedVideoData = (mp4Info.videoTracks.length === 0)
    ? undefined
    : buildEncodedVideoData(byteOffset, bytes, virtualSegment.videoSamples, mp4Info.videoTracks[0]);

  const encodedAudioData: EncodedAudioData[] = [];
  for (let i = 0; i < mp4Info.audioTracks.length; i++) {
    encodedAudioData.push(buildEncodedAudioData(byteOffset, bytes, virtualSegment.audioSamples[i], mp4Info.audioTracks[i]));
  }
  return {
    encodedVideoData,
    encodedAudioData,
  };
}
